import type { FeedbackItem, FeedbackResponse } from '../api/types';

/**
 * Unread-reply derivation for the feedback badge (side nav + collapsed rows).
 *
 * Pure on purpose: every rule below is data-in/data-out so it can be unit
 * tested without a query client, a DOM, or a signed-in user.
 *
 * Read state is the `feedbackSeen` preference (see {@link FeedbackSeen}), a map
 * of feedback id → the ISO `at` of the newest OPERATOR reply the member has
 * seen in that thread.
 *
 * Two deliberate non-rules:
 *
 *  - **Replies only, never `updatedAt`.** `updatedAt` also bumps when an
 *    operator adds an *internal* triage note, which the façade strips before
 *    the member ever sees it (`services/steward-app-api/src/transform.ts`). A
 *    badge driven by `updatedAt` could therefore light up for something the
 *    member cannot read, and so cannot clear by reading. A bare status change
 *    likewise does not count as unread.
 *  - **Key off `authorType`, never `by`.** Appcore withholds the operator's
 *    userId and sends `by: null` on their entries, so `by` cannot distinguish
 *    an operator reply from anything else.
 */

/** feedbackId → ISO `at` of the newest operator reply seen in that thread. */
export type FeedbackSeen = Record<string, string>;

/** Cap on the TOTAL number of stored entries, live and dead alike. The
 *  preferences blob has a single 8KB budget shared across ALL keys (appcore
 *  returns 400 `PREFERENCES_TOO_LARGE` past it), and `feedbackSeen` is the only
 *  key here that grows with usage. Serialized, one entry is
 *  `"<26-char ULID>":"<24-char ISO>",` = 28 + 1 + 26 + 1 = 56 bytes, so 40 of
 *  them ≈ 2.3KB (plus ~17 for the key and braces), which leaves room for the
 *  siblings. */
export const FEEDBACK_SEEN_MAX_ENTRIES = 40;

/** Parse an ISO instant to epoch ms, or null if absent/unparseable. Unparseable
 *  is treated as "no information", never as time zero: a garbled stored value
 *  should make a thread look unread (harmless, self-healing on the next read)
 *  rather than silently mark it read forever. */
function ts(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * The `at` of the newest operator reply in a thread, or null when the thread
 * holds none (an unanswered item, or one carrying only the member's own
 * follow-ups).
 *
 * Returns the original ISO string rather than a timestamp so it can be stored
 * verbatim as the seen marker.
 */
export function latestOperatorReplyAt(responses: readonly FeedbackResponse[] | undefined): string | null {
  let newest: string | null = null;
  let newestTs = -Infinity;
  for (const r of responses ?? []) {
    if (r.authorType !== 'operator') continue;
    const t = ts(r.at);
    if (t === null || t <= newestTs) continue;
    newestTs = t;
    newest = r.at;
  }
  return newest;
}

/**
 * True when this item's newest operator reply is STRICTLY newer than what the
 * member has marked seen. Strict on purpose: re-marking a thread stores the
 * exact `at` of the reply that was read, so an equal timestamp means "this is
 * the very reply you already read" — `>=` would leave the badge permanently lit.
 */
export function isFeedbackUnread(item: FeedbackItem, seen: FeedbackSeen | undefined): boolean {
  const latest = ts(latestOperatorReplyAt(item.responses));
  if (latest === null) return false; // no operator reply ⇒ nothing to read
  const seenAt = ts(seen?.[item.id]);
  if (seenAt === null) return true; // never opened (or an unreadable marker)
  return latest > seenAt;
}

/** How many items carry an unread operator reply — the nav badge count. */
export function countUnreadFeedback(items: readonly FeedbackItem[], seen: FeedbackSeen | undefined): number {
  let n = 0;
  for (const item of items) if (isFeedbackUnread(item, seen)) n++;
  return n;
}

/**
 * Cap the map at `max` entries so it cannot grow into the shared 8KB
 * preferences cap. Entries are ranked into three tiers and the whole lot is
 * then truncated once:
 *
 *  1. `pin` — the id being marked right now. It survives unconditionally,
 *     because dropping the very entry a write exists to store would leave the
 *     badge lit after the member read the thread.
 *  2. Ids present in the current list, newest marker first. These are the ones
 *     that can still be rendered, so they are the ones worth keeping.
 *  3. Everything else, newest marker first — filling only the slots tiers 1-2
 *     left over.
 *
 * Tier 3 is why absent ids are *demoted* rather than dropped. `items` is always
 * the CURRENT workspace's feed, but `feedbackSeen` hangs off the workspace-less
 * USR row and is shared across every workspace the user belongs to, and appcore
 * replaces the whole value on write. Dropping non-live ids outright therefore
 * meant that marking one thread read in workspace B erased every marker for
 * workspace A, degrading read state to "the last workspace you touched".
 * Letting them compete for the leftover slots costs nothing: the total is still
 * `max`, so the size bound documented on {@link FEEDBACK_SEEN_MAX_ENTRIES} is
 * unchanged, and a genuinely dead id (archived, deleted) is now merely the
 * first thing evicted rather than always evicted.
 *
 * Pruning at all is only possible because appcore replaces the whole
 * `feedbackSeen` value on write (it shallow-merges top-level keys only), so the
 * map we return here is exactly the map that ends up stored.
 */
export function pruneFeedbackSeen(
  seen: FeedbackSeen,
  items: readonly FeedbackItem[],
  opts: { max?: number; pin?: string } = {},
): FeedbackSeen {
  const { max = FEEDBACK_SEEN_MAX_ENTRIES, pin } = opts;
  const live = new Set(items.map(i => i.id));
  const tier = (id: string) => (id === pin ? 0 : live.has(id) ? 1 : 2);
  const kept = Object.entries(seen)
    .sort(([idA, a], [idB, b]) => tier(idA) - tier(idB) || (ts(b) ?? 0) - (ts(a) ?? 0))
    .slice(0, Math.max(0, max));
  return Object.fromEntries(kept);
}

/**
 * The next `feedbackSeen` map after the member opens thread `id`, or null when
 * no write is warranted (unknown id, no operator reply, or already seen).
 *
 * Returning null rather than an unchanged copy is load-bearing: it is what keeps
 * expanding an already-read row from firing a `PUT /me`.
 */
export function markFeedbackSeen(
  seen: FeedbackSeen | undefined,
  items: readonly FeedbackItem[],
  id: string,
): FeedbackSeen | null {
  const item = items.find(i => i.id === id);
  if (!item) return null;
  const latest = latestOperatorReplyAt(item.responses);
  if (latest === null) return null;
  const current = seen ?? {};
  if (!isFeedbackUnread(item, current)) return null;
  return pruneFeedbackSeen({ ...current, [id]: latest }, items, { pin: id });
}

/**
 * The Feedback nav item's accessible name, e.g. "Feedback, 2 new replies".
 *
 * The count has to be spoken. On the collapsed rail the only visual signal is a
 * dot, and even expanded the badge is `aria-hidden`, so without this a screen
 * reader hears a plain "Feedback" link and the whole feature is invisible to it.
 */
export function feedbackNavLabel(count: number): string {
  if (count <= 0) return 'Feedback';
  return `Feedback, ${count} new ${count === 1 ? 'reply' : 'replies'}`;
}
